import { agentToolCatalog } from "../../autogen/agentToolCatalog";
import { bffApi } from "../../autogen/bff-api";
import type { AgentToolParam } from "./agentTypes";
import type { AppDispatch } from "../redux/store";
import { withAgentToolHeader } from "./agentToolHeader";
import { contractArgAdapters as ARG_ADAPTERS, contractContextInjections as CONTEXT_INJECTIONS } from "./contractToolAdapters";

export type ToolResult =
  | { ok: true; data: unknown }
  | { ok: false; deviationCode: string; message: string };

export type ToolContext = {
  dispatch: AppDispatch;
  auth: { organizationId: string; personId: string; departmentId: string };
  // Norway's country id from baseData — the default for entities the assistant creates.
  defaultCountryId: string;
};

export const SEARCH_TOOL_NAME = "search_tender_documents";
export const DRAFT_TOOL_NAME = "draft_content";
export const OPEN_DOCUMENT_TOOL_NAME = "open_document";

// Surface the real failure to the planner so it can self-correct — including the raw body of a
// PARSING_ERROR (a non-JSON 500 body, e.g. a server validation message).
export const toError = (err: unknown): ToolResult => {
  const e = err as { status?: number | string; originalStatus?: number; data?: unknown; error?: string };
  const status = String(e?.originalStatus ?? e?.status ?? "UNKNOWN");
  const data = e?.data;
  if (data && typeof data === "object") {
    const d = data as { errorCode?: string; message?: string };
    return { ok: false, deviationCode: d.errorCode ?? status, message: d.message ?? d.errorCode ?? JSON.stringify(data) };
  }
  const raw = typeof data === "string" && data.trim() ? data.trim() : e?.error;
  return { ok: false, deviationCode: status, message: raw ? `${status}: ${raw}` : "Forespørselen feilet." };
};

type EndpointInitiator = { initiate: (arg: Record<string, unknown>) => unknown };

// An adapter fully assembles the request for an endpoint the OpenAPI spec can't describe as
// model-facing params. The domain registry it lives in is composed in via the import above.
export type ArgAdapter = (args: Record<string, unknown>, ctx: ToolContext) => Record<string, unknown>;

// A declarative injection for endpoints whose real request carries client context the model must not
// supply. The engine folds the catalog's advertised body params under bodyKey, adds the context
// defaults, and sets the path params — no per-tool imperative code.
export type ContextInjection = {
  bodyKey: string;
  pathParams?: Record<string, (ctx: ToolContext) => unknown>;
  bodyDefaults?: Record<string, (ctx: ToolContext) => unknown>;
};

const descriptorFor = (operationId: string) => agentToolCatalog.find((tool) => tool.toolName === operationId);

const bodyParamNames = (operationId: string): string[] =>
  descriptorFor(operationId)?.params.filter((p) => p.in === "body").map((p) => p.name) ?? [];

const applyContextInjection = (
  operationId: string,
  injection: ContextInjection,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Record<string, unknown> => {
  const nested = args[injection.bodyKey];
  const nestedBody = nested && typeof nested === "object" ? (nested as Record<string, unknown>) : {};
  const body: Record<string, unknown> = {};
  for (const name of bodyParamNames(operationId)) {
    const value = args[name] ?? nestedBody[name];
    if (value !== undefined) {
      body[name] = value;
    }
  }
  for (const [name, produce] of Object.entries(injection.bodyDefaults ?? {})) {
    body[name] = produce(ctx);
  }
  const result: Record<string, unknown> = { [injection.bodyKey]: body };
  for (const [name, produce] of Object.entries(injection.pathParams ?? {})) {
    result[name] = produce(ctx);
  }
  return result;
};

// Assemble the real request from the model's args and the client context: a bespoke adapter for the
// endpoints that need one, a declarative context injection for the rest, else the args verbatim.
export const buildEndpointArgs = (
  operationId: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Record<string, unknown> => {
  const adapter = ARG_ADAPTERS[operationId];
  if (adapter) {
    return adapter(args, ctx);
  }
  const injection = CONTEXT_INJECTIONS[operationId];
  if (injection) {
    return applyContextInjection(operationId, injection, args, ctx);
  }
  return args;
};

// Deviations mirror the ones runTool's own callers (e.g. generateDraft's MISSING_INSTRUCTION) return
// on bad input, so the planner sees a tool result it can self-correct from rather than a silent
// dispatch of degenerate data.
const missingFieldError = (name: string): ToolResult => ({
  ok: false,
  deviationCode: "MISSING_FIELD",
  message: `Feltet '${name}' er påkrevd — fyll det ut før verktøyet kjøres.`,
});

// A blank string on a field the schema marks minLength >= 1 would silently clear the field
// server-side (the ConfirmationCard's hasValue() treats a blank as "not present" and shows nothing
// about it), so reject it before it reaches the endpoint.
const blankFieldError = (name: string): ToolResult => ({
  ok: false,
  deviationCode: "BLANK_FIELD",
  message: `Feltet '${name}' kan ikke være tomt — utelat felt som ikke skal endres.`,
});

const isBlankString = (value: unknown, schema: Record<string, unknown>): boolean =>
  typeof value === "string" &&
  typeof schema.minLength === "number" &&
  schema.minLength >= 1 &&
  value.trim().length === 0;

// Resolve a param's value from the model's args, tolerating a body param the model nested under the
// injection's bodyKey rather than supplying flat (the shape the catalog advertises).
const paramValue = (
  args: Record<string, unknown>,
  param: AgentToolParam,
  bodyKey: string | undefined,
): unknown => {
  const direct = args[param.name];
  if (direct !== undefined) {
    return direct;
  }
  if (param.in === "body" && bodyKey) {
    const nested = args[bodyKey];
    if (nested && typeof nested === "object") {
      return (nested as Record<string, unknown>)[param.name];
    }
  }
  return undefined;
};

const missingObjectFieldError = (value: Record<string, unknown>, schema: Record<string, unknown>): ToolResult | null => {
  const required = schema.required as string[] | undefined;
  for (const name of required ?? []) {
    if (value[name] === undefined || value[name] === null) {
      return missingFieldError(name);
    }
  }
  return null;
};

// Walk a present object's leaves against its schema and reject any blank string whose schema demands
// a minimum length. Required is enforced only at the object's top level (by the caller) so a
// legitimate partial update of a nested object is not mistaken for a missing field.
const blankLeafError = (value: unknown, schema: Record<string, unknown>): ToolResult | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const properties = schema.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties) {
    return null;
  }
  for (const [name, leaf] of Object.entries(value as Record<string, unknown>)) {
    const leafSchema = properties[name];
    if (!leafSchema) {
      continue;
    }
    if (isBlankString(leaf, leafSchema)) {
      return blankFieldError(name);
    }
    const nested = blankLeafError(leaf, leafSchema);
    if (nested) {
      return nested;
    }
  }
  return null;
};

// Validate the model's args against the tool's real JSON Schema in the catalog: required params must
// be present and any present string the schema marks minLength >= 1 must be non-blank. Purely
// schema-driven — no per-tool code. The executor injects auth/context params, which the catalog does
// not advertise, so they are never flagged.
export const validateToolArgs = (operationId: string, args: Record<string, unknown>): ToolResult | null => {
  const descriptor = descriptorFor(operationId);
  if (!descriptor) {
    return null;
  }
  const bodyKey = CONTEXT_INJECTIONS[operationId]?.bodyKey;
  for (const param of descriptor.params) {
    const value = paramValue(args, param, bodyKey);
    if (param.required && (value === undefined || value === null)) {
      return missingFieldError(param.name);
    }
    if (value === undefined || value === null) {
      continue;
    }
    if (param.schema.type === "object" && value && typeof value === "object" && !Array.isArray(value)) {
      const missing = missingObjectFieldError(value as Record<string, unknown>, param.schema);
      if (missing) {
        return missing;
      }
      const blank = blankLeafError(value, param.schema);
      if (blank) {
        return blank;
      }
    } else if (isBlankString(value, param.schema)) {
      return blankFieldError(param.name);
    }
  }
  return null;
};

// Generic execution: any catalog tool is just an operationId. Auth/org/person context is injected
// here; the model only supplies the domain params advertised by the catalog. buildEndpointArgs shapes
// the request for the rare endpoint that needs it.
export const runEndpointTool = async (
  operationId: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Promise<ToolResult> => {
  const endpoints = bffApi.endpoints as unknown as Record<string, EndpointInitiator>;
  const endpoint = endpoints[operationId];
  if (!endpoint) {
    return { ok: false, deviationCode: "UNKNOWN_TOOL", message: `Ukjent verktøy: ${operationId}` };
  }
  const validationError = validateToolArgs(operationId, args);
  if (validationError) {
    return validationError;
  }
  const authContext = {
    organizationId: ctx.auth.organizationId,
    personId: ctx.auth.personId,
    orgId: ctx.auth.organizationId,
  };
  const finalArgs = buildEndpointArgs(operationId, args, ctx);
  try {
    const data = await withAgentToolHeader(operationId, () => {
      const thunk = endpoint.initiate({ ...authContext, ...finalArgs });
      const dispatched = ctx.dispatch(thunk as never) as unknown as { unwrap: () => Promise<unknown> };
      return dispatched.unwrap();
    });
    return { ok: true, data };
  } catch (err) {
    return toError(err);
  }
};

export const runTool = async (
  toolName: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
  generateDraft: (args: Record<string, unknown>) => Promise<ToolResult>,
): Promise<ToolResult> => {
  if (toolName === DRAFT_TOOL_NAME) {
    return generateDraft(args);
  }
  return runEndpointTool(toolName, args, ctx);
};

const PLANNER_RESULT_MAX_CHARS = 6000;

// The planner only needs to know what a tool returned (and any ids for chaining), not the full
// payload. Bound it generically so the planning model stays fast; the answer phase always
// receives the full result. The budget fits a typical list so the planner can scan it without
// re-listing; on overflow the marker points at pagination (skip/limit) instead of a blind re-call
// with the same arguments, which would just truncate identically.
export const boundForPlanner = (result: ToolResult): string => {
  const json = result.ok ? JSON.stringify(result.data) : JSON.stringify(result);
  if (json.length <= PLANNER_RESULT_MAX_CHARS) {
    return json;
  }
  return (
    `${json.slice(0, PLANNER_RESULT_MAX_CHARS)}… [forkortet — de første resultatene vises. ` +
    "Trenger du flere: kall verktøyet med skip/limit for neste side; ikke gjenta kallet med samme argumenter]"
  );
};
